import React, { useMemo, useState } from 'react';
import {
  useTable,
  SortingState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import { DataTable } from '../components/common/DataTable';
import { createDotNetUpdateColumns } from '../components/dotnet-updates/dotNetUpdateColumns';
import { formatBytes } from '../components/common/tableCells';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  Upload as UploadIcon,
} from '@mui/icons-material';
import DeployDialog from '../components/deployments/DeployDialog';
import RecentDeployments from '../components/deployments/RecentDeployments';
import errorService from '@services/errorService';
import {
  useGetDotNetAppUpdatesQuery,
  useUploadDotNetAppUpdateMutation,
  useDeleteDotNetAppUpdateMutation,
} from '@store/api/dotnetAppUpdatesApi';

const DotNetAppUpdatesPage: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: updates = [], isLoading: loading, error: queryError } = useGetDotNetAppUpdatesQuery();
  const [uploadUpdate] = useUploadDotNetAppUpdateMutation();
  const [deleteUpdate] = useDeleteDotNetAppUpdateMutation();

  const [error, setError] = useState<string | null>(null);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress] = useState(0);


  const handleUpload = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    // Extract version from filename
    // Format: comnovogateway-{version}-arm64.tar.gz
    const match = uploadFile.name.match(/comnovogateway-(.*)-arm64\.tar\.gz/);
    if (!match) {
      setError('Invalid filename format. Expected: comnovogateway-{version}-arm64.tar.gz');
      return;
    }
    const version = match[1];

    // Check for signature file
    if (!signatureFile) {
      setError('Please select both the .tar.gz file and its .sig.base64 signature file.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Read signature file
      const signatureBase64 = await signatureFile.text();

      await uploadUpdate({
        file: uploadFile,
        version,
        signature_base64: signatureBase64,
      }).unwrap();

      setUploadDialogOpen(false);
      setUploadFile(null);
      setSignatureFile(null);
    } catch (err: any) {
      setError(errorService.toUserMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Find .tar.gz file and .sig.base64 file
    const tarFile = Array.from(files).find(f => f.name.endsWith('.tar.gz'));
    const sigFile = Array.from(files).find(f => f.name.endsWith('.sig.base64'));

    if (tarFile) {
      setUploadFile(tarFile);
    }
    if (sigFile) {
      setSignatureFile(sigFile);
    }
  };

  const handleDelete = async (version: string) => {
    if (!confirm(`Are you sure you want to delete version ${version}?`)) {
      return;
    }

    try {
      setError(null);
      await deleteUpdate(version).unwrap();
    } catch (err: any) {
      setError(errorService.toUserMessage(err));
    }
  };

  const handleDownload = async (version: string) => {
    try {
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = `/api/v1/dotnet-app-updates/download/${encodeURIComponent(version)}`;
      link.setAttribute('download', `comnovogateway-${version}-arm64.tar.gz`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to download update');
    }
  };

  const updateColumns = useMemo(
    () =>
      createDotNetUpdateColumns({
        onDeploy: setSelectedVersion,
        onDownload: handleDownload,
        onDelete: handleDelete,
      }),
    // Safe to build once: the handlers only touch state setters and RTK
    // mutation triggers, all of which keep their identity across renders.
    []
  );

  const updatesTable = useTable({
    features,
    data: updates,
    columns: updateColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId: (row) => row.version,
  });

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
        <Typography variant="h4">.NET Application Updates</Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload Update
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {queryError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load updates. Please try again.
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <DataTable
          table={updatesTable}
          ariaLabel=".NET app updates"
          isLoading={loading}
          emptyMessage="No .NET app updates available. Upload your first update to get started."
          minWidth={1000}
          // Deleted updates stay listed but read as inactive.
          getRowSx={(update) =>
            update.is_deleted
              ? { opacity: 0.5, bgcolor: 'action.disabledBackground' }
              : {}
          }
        />
      </Paper>

      <RecentDeployments kind="dotnet" />

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload .NET App Update</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Upload the signed update package (.tar.gz) along with its signature file (.sig.base64).
            Both files should be generated by the packaging script.
          </Alert>
          <TextField
            type="file"
            fullWidth
            onChange={handleFileChange}
            disabled={uploading}
            helperText="Select both files: comnovogateway-{version}-arm64.tar.gz and .sig.base64"
            sx={{ mb: 2 }}
            slotProps={{
              htmlInput: {
                accept: '.tar.gz,.sig.base64',
                multiple: true,
              }
            }}
          />
          {uploadFile && (
            <Typography variant="body2" sx={{
              color: 'text.secondary'
            }}>
              Package: {uploadFile.name} ({formatBytes(uploadFile.size)})
            </Typography>
          )}
          {signatureFile && (
            <Typography variant="body2" sx={{
              color: 'success.main'
            }}>
              Signature: {signatureFile.name} ✓
            </Typography>
          )}
          {uploadFile && !signatureFile && (
            <Typography variant="body2" sx={{
              color: 'warning.main'
            }}>
              Warning: No signature file selected
            </Typography>
          )}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: 'text.secondary',
                  mt: 1
                }}>
                Uploading: {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} variant="contained" disabled={!uploadFile || !signatureFile || uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeployDialog
        key={selectedVersion ?? ''}
        kind="dotnet"
        version={selectedVersion}
        onClose={() => setSelectedVersion(null)}
        notice={
          <Alert severity="warning">
            The update script stops the .NET application, replaces its files and restarts it on
            each selected device.
          </Alert>
        }
      />
    </Box>
  );
};

export default DotNetAppUpdatesPage;
